import React, { useState, useEffect } from 'react';
import EsperandoPagoModal from './EsperandoPagoModal';
import { formatearMonto } from '../../api/portalEstudiantes';
import styles from '../../styles/PortalEstudiantesStyles/PortalEstudiantes.module.css';

/**
 * Modal de confirmación de pago
 * Prepara el flujo hacia la interfaz bancaria externa
 * 
 * @param {Object} cargo - Cargo a pagar (id, concepto, saldo_pendiente, fecha_vencimiento)
 * @param {Function} onConfirm - Callback al confirmar (recibe referencia bancaria)
 * @param {Function} onCancel - Callback al cancelar
 */

// Extrae el número de mes de un periodo actual tipo "2026-01" o "Enero 2026"
function extraerMesDePeriodo(periodoActual) {
  if (!periodoActual) return '';
  // Si es tipo "2026-01", "2026-1", etc.
  const match = periodoActual.match(/\d{4}-(\d{1,2})/);
  if (match) return String(match[1]).padStart(2, '0');
  // Si es tipo "Enero 2026" o "Agosto 2026"
  const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
  const lower = periodoActual.toLowerCase();
  for (let i = 0; i < meses.length; i++) {
    if (lower.includes(meses[i])) return String(i+1).padStart(2, '0');
  }
  return '';
}

function generarReferencia(matricula, periodoActual) {
  let periodoConcat = '';
  if (periodoActual) {
    // Extrae año y mes, y concatena: primer dígito del año + últimos 2 dígitos del año + mes sin ceros a la izquierda
    const match = periodoActual.match(/(\d{4})-(\d{1,2})/);
    if (match) {
      // Ejemplo: 2026 -> 2 + 26, 2025 -> 2 + 25
      const anio = match[1];
      const periodo = anio[0] + anio.slice(2) + String(Number(match[2]));
      periodoConcat = periodo;
    } else {
      // Si no es formato esperado, solo usa los últimos 4 dígitos del periodoActual sin espacios ni guiones
      periodoConcat = periodoActual.replace(/[^\d]/g, '').slice(-4);
    }
  }
  const random = Math.floor(100000 + Math.random() * 900000);
  return `${matricula || ''}${periodoConcat}${random}`;
}



function formatReferenciaEspaciada(ref) {
  return (ref || '').replace(/(.{4})/g, '$1 ').trim();
}


function ConfirmarPagoModal({ cargo, matricula = '', periodoActual = '', referenciaBancaria, pagoPendienteInfo, onConfirm, onCancel, refrescarEstadoCuenta }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEsperandoPago, setShowEsperandoPago] = useState(false);

  if (!cargo) return null;

  const montoBase = Number(cargo.saldo_pendiente) || 0;
  const recargo = Number(cargo.recargo) || 0;
  const subtotalPago = montoBase + recargo;
  const comisionBanco = subtotalPago * 0.02;
  const totalPagar = subtotalPago + comisionBanco;

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      // Enviar datos al backend para generar liga WebPay
      const response = await fetch('/api/financiero/webpay/generar-liga', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          referencia: referenciaBancaria || cargo.id,
          monto: totalPagar,
        }),
      });
      const data = await response.json();
      if (data.liga_pago) {
        window.open(data.liga_pago, '_blank'); // Abre la liga de pago en una nueva pestaña
        setShowEsperandoPago(true);
      } else {
        setError('No se pudo generar la liga de pago.');
      }
    } catch (err) {
      setError('Error al conectar con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.modalOverlay} onClick={onCancel}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3 className={styles.modalTitle}>Confirmar Pago</h3>
          </div>

          <div className={styles.modalBody}>
            <div className={styles.pagoDetalles}>
              <div className={styles.detalleRow}>
                <span className={styles.detalleLabel}>Concepto a pagar</span>
              </div>
              <div className={styles.detalleRow}>
                <span className={styles.detalleLabel}>Concepto:</span>
                <span className={styles.detalleValue}>{cargo.concepto}</span>
              </div>
              <div className={styles.detalleRow}>
                <span className={styles.detalleLabel}>Vencimiento:</span>
                <span className={styles.detalleValue}>{cargo.fecha_vencimiento}</span>
              </div>
              <div className={styles.detalleRow}>
                <span className={styles.detalleLabel}>Monto:</span>
                <span className={styles.detalleValue}>
                  {formatearMonto(montoBase)}
                </span>
              </div>

              <div className={styles.detalleSeparator} />

              <div className={`${styles.detalleRow} ${styles.totalRow}`}>
                <span className={styles.detalleLabel}>Total a pagar:</span>
                <span className={styles.detalleTotalValue}>
                  {formatearMonto(totalPagar)}
                  {recargo ? (
                    <span style={{ color: '#c00', fontSize: '0.9em', marginLeft: 8 }}>
                      (incluye recargo de {formatearMonto(recargo)})
                    </span>
                  ) : null}
                </span>
              </div>
              <div className={styles.detalleRow} style={{ alignItems: 'flex-start' }}>
                <span className={styles.detalleValue} style={{ fontSize: '0.92em', color: '#666', lineHeight: 1.4 }}>
                  Se aplicara un 2% de comision bancaria al momento de procesar el pago, ya incluida en el total mostrado.
                </span>
              </div>

              {/* Campo para referencia bancaria (preparación para pago externo) */}
              <div className={styles.referenciaSection}>
                <label className={styles.referenciaLabel}>
                  Referencia:
                </label>
                <input
                  type="text"
                  className={styles.referenciaInput}
                  value={formatReferenciaEspaciada(referenciaBancaria)}
                  readOnly
                />
              </div>

              <div className={styles.avisoExternoContainer}>
                <span className={styles.avisoExternoIcon}>ℹ️</span>
                <p className={styles.avisoExternoText}>
                  Al confirmar, serás redirigido a la plataforma de pago seguro.
                  Tu pago quedará pendiente de validación por el administrador.
                </p>
              </div>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button 
              className={styles.cancelButton}
              onClick={onCancel}
              disabled={loading}
            >
              CANCELAR
            </button>
            <button 
              className={styles.confirmButton}
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading ? 'Procesando...' : 'PROCEDER AL PAGO'}
            </button>
          </div>
          {error && <div className={styles.errorMsg}>{error}</div>}
        </div>
      </div>
      {showEsperandoPago && (
        <EsperandoPagoModal
          referencia={(referenciaBancaria || '').replace(/\s+/g, '')}
          onClose={(infoPago) => {
            // Si el pago fue exitoso, cerrar ambos modales
            setShowEsperandoPago(false);
            onCancel(); // Cierra ConfirmarPagoModal
            if (refrescarEstadoCuenta) refrescarEstadoCuenta();
          }}
        />
      )}
    </>
  );
}

export default ConfirmarPagoModal;
